const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || '';

const isAbsoluteUrl = (value = '') => /^https?:\/\//i.test(value);

const baseUrl = () => {
  if (!CLOUDINARY_CLOUD_NAME) return '';
  return `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}`;
};

const buildTransform = ({ width, extra = [] } = {}) => {
  const transforms = ['f_auto', 'q_auto'];
  if (width) transforms.push(`w_${width}`);
  return transforms.concat(extra).join(',');
};

const buildCloudinaryUrl = ({
  resourceType,
  publicId,
  width,
  extraTransforms,
  format,
}) => {
  if (!publicId || !baseUrl()) return '';
  const transform = buildTransform({ width, extra: extraTransforms });
  const suffix = format ? `.${format}` : '';
  return `${baseUrl()}/${resourceType}/upload/${transform}/${publicId}${suffix}`;
};

export const cloudinaryImageUrl = (publicId, options = {}) => buildCloudinaryUrl({
  resourceType: 'image',
  publicId,
  width: options.width,
  extraTransforms: options.transforms,
  format: options.format,
});

export const cloudinaryVideoUrl = (publicId, options = {}) => buildCloudinaryUrl({
  resourceType: 'video',
  publicId,
  width: options.width,
  extraTransforms: options.transforms,
  format: options.format,
});

export const cloudinaryVideoPosterUrl = (publicId, options = {}) => buildCloudinaryUrl({
  resourceType: 'video',
  publicId,
  width: options.width,
  extraTransforms: ['so_0'].concat(options.transforms || []),
  format: options.format || 'jpg',
});

export const cloudinaryPreviewFromVideoUrl = (url, options = {}) => {
  if (!url || !url.includes('/video/upload/')) return url || '';
  const [prefix, suffixRaw] = url.split('/video/upload/');
  if (!prefix || !suffixRaw) return url;
  const segments = suffixRaw.split('/').filter(Boolean);
  if (!segments.length) return url;

  // Drop any existing transformation segment to avoid duplicated transforms.
  const first = segments[0];
  if (first.includes(',')) {
    segments.shift();
  }

  const width = options.width || 640;
  const transform = `f_auto,q_auto:eco,w_${width},so_0`;
  return `${prefix}/video/upload/${transform}/${segments.join('/')}`;
};

export const resolveMediaUrl = ({ url, publicId, type = 'image', width, format } = {}) => {
  if (url) {
    if (isAbsoluteUrl(url)) return url;
    if (url.startsWith('/')) return url;
  }
  if (!publicId) return '';
  if (type === 'video') {
    return cloudinaryVideoUrl(publicId, { width, format });
  }
  return cloudinaryImageUrl(publicId, { width, format });
};

export const resolvePosterUrl = ({ url, publicId, width, format = 'jpg' } = {}) => {
  if (url) {
    if (isAbsoluteUrl(url)) return url;
    if (url.startsWith('/')) return url;
  }
  if (!publicId) return '';
  return cloudinaryVideoPosterUrl(publicId, { width, format });
};

export const getMuxPlaybackUrl = (playbackId) => {
  if (!playbackId) return '';
  return `https://stream.mux.com/${playbackId}.m3u8`;
};

export const getMuxPosterUrl = (playbackId, options = {}) => {
  if (!playbackId) return '';
  const time = options.time || 0;
  const width = options.width ? `&width=${options.width}` : '';
  return `https://image.mux.com/${playbackId}/thumbnail.jpg?time=${time}${width}`;
};

export const cloudinarySmartThumbUrl = (url, options = {}) => {
  if (!url || !url.includes('res.cloudinary.com') || !url.includes('/upload/')) return url || '';

  const [withoutQuery, queryString] = url.split('?');
  const [prefix, suffixRaw] = withoutQuery.split('/upload/');
  if (!prefix || !suffixRaw) return url;

  const segments = suffixRaw.split('/').filter(Boolean);
  if (!segments.length) return url;

  // Remove existing transformation segment when present.
  if (segments[0].includes(',')) {
    segments.shift();
  }

  const width = options.width || 640;
  const height = options.height || 360;
  const gravity = options.gravity || 'g_auto:subject';
  const transform = `f_auto,q_auto,c_fill,w_${width},h_${height},${gravity}`;
  const rebuilt = `${prefix}/upload/${transform}/${segments.join('/')}`;

  return queryString ? `${rebuilt}?${queryString}` : rebuilt;
};
